import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, from, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AuthSelectors } from '@mona/auth';
import { ConfigService, WEB_BROWSER_DEFAULT_POSITION_PARAMS } from '@mona/config';
import { IpcMainEvent } from '@mona/events';
import { ExternalResource } from '@mona/models';
import { isEmptyEncounterId, currentEncounterId } from '@mona/pdms/data-access-combined';
import { EncounterService } from '@mona/pdms/data-access-encounter-data';
import { ExternalResourcesApi, ExternalResourcesState } from '@mona/pdms/data-access-external-resources';
import { MonaRpcService } from '@mona/rpc';
import { isSameEncounterFilter } from '@mona/shared/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ExternalResourcesGroup } from '../../../../../../shared/models/src/lib/external-resourses-group.model';
import { ExternalResourcesActions } from '../actions/external-resources.actions';

/**
 * Notifications effects
 */
@Injectable()
export class ExternalResourcesEffects {
    /** Load External Resources */
    loadExternalResources$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalResourcesActions.loadExternalResources),
            switchMap(() =>
                combineLatest([
                    this.encounterService.getEncounter().pipe(
                        catchError(() => of(null)),
                        filter(encounter => !!encounter),
                    ),
                    this.store.select(AuthSelectors.selectUser),
                ]),
            ),
            switchMap(([encounter, user]) =>
                this.externalResourcesApiService
                    .getExternalResources(encounter, user)
                    .pipe(
                        isSameEncounterFilter(currentEncounterId(this.store), encounter.id),
                        takeUntil(isEmptyEncounterId(this.store)),
                    ),
            ),
            map((data: ExternalResource[]) => {
                const groupedData = data.reduce(
                    (acc, link) => {
                        const applicationName = link.application?.name || 'No Application';
                        if (!link.application) {
                            acc.noApplicationLinks.push(link);
                        } else {
                            if (!acc.groupedLinks[applicationName]) {
                                acc.groupedLinks[applicationName] = {
                                    ...link.application,
                                    resources: [],
                                };
                            }
                            acc.groupedLinks[applicationName].resources.push(link);
                        }
                        return acc;
                    },
                    {
                        groupedLinks: {} as Record<string, ExternalResourcesGroup>,
                        noApplicationLinks: [] as ExternalResource[],
                    },
                );

                const aggregatedData = {
                    internalApplications: Object.values(groupedData.groupedLinks),
                    externalApplications: groupedData.noApplicationLinks,
                };

                return ExternalResourcesActions.loadExternalResourcesSuccess(aggregatedData);
            }),
            catchError(error => of(ExternalResourcesActions.loadExternalResourcesFailure({ error }))),
        ),
    );
    browserNavigateBackAction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalResourcesActions.browserNavigateBackAction),
            exhaustMap(() =>
                from(this.rpcService.invoke<any>(IpcMainEvent.BROWSER_NAVIGATE_BACK)).pipe(
                    catchError(err => {
                        console.error(err);
                        return of();
                    }),
                ),
            ),
            map(() => ExternalResourcesActions.browserNavigateBackActionSuccess()),
        ),
    );
    browserNavigateForwardAction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalResourcesActions.browserNavigateForwardAction),
            exhaustMap(() =>
                from(this.rpcService.invoke<any>(IpcMainEvent.BROWSER_NAVIGATE_FORWARD)).pipe(
                    catchError(err => {
                        console.error(err);
                        return of();
                    }),
                ),
            ),
            map(() => ExternalResourcesActions.browserNavigateForwardActionSuccess()),
        ),
    );
    browserReloadAction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalResourcesActions.browserReloadAction),
            exhaustMap(() =>
                from(this.rpcService.invoke<any>(IpcMainEvent.BROWSER_RELOAD)).pipe(
                    catchError(err => {
                        console.error(err);
                        return of();
                    }),
                ),
            ),
            map(() => ExternalResourcesActions.browserReloadActionSuccess()),
        ),
    );
    browserEndSessionAction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalResourcesActions.browserEndSessionAction),
            exhaustMap(() =>
                from(this.rpcService.invoke<any>(IpcMainEvent.BROWSER_END_SESSION)).pipe(
                    catchError(err => {
                        console.error(err);
                        return of();
                    }),
                ),
            ),
            map(() => ExternalResourcesActions.browserEndSessionActionSuccess()),
        ),
    );
    browserViewResize = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalResourcesActions.browserViewResize),
            exhaustMap(payload =>
                from(this.rpcService.invoke<any>(IpcMainEvent.BROWSER_VIEW_RESIZE, payload)).pipe(
                    catchError(err => {
                        console.error(err);
                        return of();
                    }),
                    map(() => ExternalResourcesActions.browserViewResizeSuccess()),
                ),
            ),
        ),
    );
    browserSetWindowShownAction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalResourcesActions.browserSetWindowShownAction),
            exhaustMap(() =>
                from(
                    this.rpcService.invoke<any>(
                        IpcMainEvent.BROWSER_SET_WINDOW_SHOWN,
                        this.configService.get('webBrowserPositionParams') || WEB_BROWSER_DEFAULT_POSITION_PARAMS,
                    ),
                ).pipe(
                    catchError(err => {
                        console.error(err);
                        return of();
                    }),
                ),
            ),
            map(() => ExternalResourcesActions.browserSetWindowShownActionSuccess()),
        ),
    );
    browserSetWindowHiddenAction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ExternalResourcesActions.browserSetWindowHiddenAction),
            exhaustMap(() =>
                from(this.rpcService.invoke<any>(IpcMainEvent.BROWSER_SET_WINDOW_HIDDEN)).pipe(
                    catchError(err => {
                        console.error(err);
                        return of();
                    }),
                ),
            ),
            map(() => ExternalResourcesActions.browserSetWindowHiddenActionSuccess()),
        ),
    );

    /**
     * Constructor
     *
     * @param actions$
     * @param externalResourcesApiService
     * @param rpcService
     * @param configService
     * @param encounterService
     * @param store
     */
    constructor(
        private actions$: Actions,
        private externalResourcesApiService: ExternalResourcesApi,
        private rpcService: MonaRpcService,
        private configService: ConfigService,
        private encounterService: EncounterService,
        private store: Store<ExternalResourcesState>,
    ) {}
}
